import { useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { UUID } from '@/types/common';
import { 
  CreateJobCardRequest, 
  UpdateJobCardRequest 
} from '@/types/workOrders';
import { WorkOrderBaseState } from './workOrderBase';

export const useWorkOrderJobCards = (baseState: WorkOrderBaseState) => {
  const {
    loading,
    setLoading,
    workOrderOperations,
    toast
  } = baseState;

  /**
   * Fetch Job Cards for a Work Order Operation
   */
  const fetchJobCards = useCallback(async (operationId: UUID) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('job_cards')
        .select(`
          *,
          users:worker_id (
            id,
            full_name,
            email
          )
        `)
        .eq('work_order_operation_id', operationId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data;
    } catch (error: any) {
      toast({
        title: 'Error fetching job cards',
        description: error.message,
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [setLoading, toast]);

  /**
   * Create a Job Card for an operation
   */
  const createJobCard = useCallback(async (jobCard: CreateJobCardRequest) => {
    try {
      setLoading(true);
      
      // Get the work order ID from the work order operation
      let workOrderId = null;
      if (jobCard.work_order_operation_id) {
        const { data: operationData } = await supabase
          .from('work_order_operations')
          .select('work_order_id')
          .eq('id', jobCard.work_order_operation_id)
          .single();
          
        if (operationData) {
          workOrderId = operationData.work_order_id;
        }
      }
      
      if (!workOrderId) {
        throw new Error("Could not determine work order for this operation");
      }
      
      // Make sure the card includes all the required fields
      const completeJobCard = {
        work_order_operation_id: jobCard.work_order_operation_id,
        worker_id: jobCard.worker_id, 
        // Note: work_order_id is now set automatically by the database trigger
        quantity: jobCard.quantity || 1,
        remarks: jobCard.remarks || '',
        status: 'Not Started',
        planned_start_time: new Date().toISOString(),
        planned_end_time: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Default to 24 hours later
        // Explicitly set these to null to avoid any confusion
        actual_start_time: null,
        actual_end_time: null,
        // job_card_number will be auto-generated by DB trigger
        // created_by will be set by the database trigger
      };
      
      // Using upsert with onConflict option for more robust operation
      const { data, error } = await supabase
        .from('job_cards')
        .insert([completeJobCard])
        .select()
        .single();
      
      if (error) {
        console.error("Job card creation error:", error);
        throw error;
      }
      
      toast({
        title: 'Job Card created',
        description: `Job Card ${data.job_card_number} has been created successfully.`,
        variant: 'success',
      });
      
      return data;
    } catch (error: any) {
      toast({
        title: 'Error creating job card',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [setLoading, toast]);

  /**
   * Update a Job Card
   */
  const updateJobCard = useCallback(async (jobCard: UpdateJobCardRequest) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('job_cards')
        .update(jobCard)
        .eq('id', jobCard.id)
        .select()
        .single();
      
      if (error) throw error;
      
      toast({
        title: 'Job Card updated',
        description: 'Job Card has been updated successfully.',
        variant: 'success',
      });
      
      return data;
    } catch (error: any) {
      toast({
        title: 'Error updating job card',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [setLoading, toast]);

  /**
   * Start a Job Card
   */
  const startJobCard = useCallback(async (jobCardId: UUID) => {
    try {
      setLoading(true);
      
      // Update job card status and set actual start time
      const { data, error } = await supabase
        .from('job_cards')
        .update({ 
          status: 'In Progress', 
          actual_start_time: new Date().toISOString() 
        })
        .eq('id', jobCardId)
        .select('*, work_order_operation:work_order_operation_id(*)')
        .single();
      
      if (error) throw error;
      
      // If this is the first job card to be started for this operation,
      // also update the operation status
      if (data.work_order_operation && data.work_order_operation.status === 'Not Started') {
        await supabase
          .from('work_order_operations')
          .update({ 
            status: 'In Progress',
            actual_start_time: new Date().toISOString() 
          })
          .eq('id', data.work_order_operation_id);
      }
      
      toast({
        title: 'Job Card started',
        description: 'Job Card has been started successfully.',
        variant: 'success',
      });
      
      return data;
    } catch (error: any) {
      toast({
        title: 'Error starting job card',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [setLoading, toast]);

  /**
   * Complete a Job Card
   */
  const completeJobCard = useCallback(async (jobCardId: UUID) => {
    try {
      setLoading(true);
      
      // Update job card status and set actual end time
      const { data, error } = await supabase
        .from('job_cards')
        .update({ 
          status: 'Completed', 
          actual_end_time: new Date().toISOString() 
        })
        .eq('id', jobCardId)
        .select('*, work_order_operation:work_order_operation_id(*)')
        .single();
      
      if (error) throw error;
      
      toast({
        title: 'Job Card completed',
        description: 'Job Card has been completed successfully.',
        variant: 'success',
      });
      
      // Check if all job cards for this operation are completed
      const { data: jobCards, error: jobCardsError } = await supabase
        .from('job_cards')
        .select('status')
        .eq('work_order_operation_id', data.work_order_operation_id);
      
      if (!jobCardsError && jobCards && jobCards.every(jc => jc.status === 'Completed')) {
        // If all job cards are completed, prompt to complete the operation
        toast({
          title: 'All job cards completed',
          description: 'You can now complete the operation.',
          variant: 'info',
        });
      }
      
      return data;
    } catch (error: any) {
      toast({
        title: 'Error completing job card',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [setLoading, toast]);

  return {
    loading,
    fetchJobCards,
    createJobCard,
    updateJobCard,
    startJobCard,
    completeJobCard
  };
};
